import { formatDuration, formatMoney, formatNumber, formatPercent, t } from '@glory/i18n';
import { type BasisPoints, bp, type Kopecks, kopecks, minutes, type Minutes } from '@glory/kernel';
import { type ChangeEvent, type ReactNode, useLayoutEffect, useRef } from 'react';

import { cn } from '../lib/cn.ts';
import { type BoxProps, useBoxState, useDraft } from './draft.tsx';
import { Field, FieldBox, type FieldPart, inputClass, splitField } from './field.tsx';
import {
  formatHoursInput,
  formatMoneyInput,
  formatMultiplierInput,
  formatNumberInput,
  formatPercentInput,
  groupDigitsLive,
  parseHoursInput,
  parseMoneyInput,
  parseMultiplierInput,
  parseNumberInput,
  parsePercentInput,
} from './parsers/number.ts';

type Draft<T> = ReturnType<typeof useDraft<T>>;

/** Свойства числовых полей: границы и шаг стрелок в единицах хранения (копейки, bp, минуты). */
interface NumericProps<T extends number> extends Omit<BoxProps, 'defaultOpen' | 'today'> {
  readonly value: T | null;
  readonly onValueChange: (value: T | null) => void;
  readonly min?: T | undefined;
  readonly max?: T | undefined;
  /** Шаг стрелок ↑↓; с Shift — вдесятеро больше. */
  readonly step?: T | undefined;
}

/** Правила вида: как показать, разобрать и сдвинуть стрелкой значение этого поля. */
interface NumericKind<T extends number> {
  readonly parse: (text: string) => T | null;
  readonly format: (value: T) => string;
  /** Как показать значение в подсказках и ошибках: «120 000 ₽», «40 %». */
  readonly show: (value: T) => string;
  readonly make: (value: number) => T;
  readonly invalid: string;
  readonly hint?: string;
  readonly step: T;
  readonly allowNegative?: boolean;
  readonly integerOnly?: boolean;
  readonly leading?: ReactNode;
  readonly trailing?: ReactNode;
  /** Разряды пробелами прямо при вводе. */
  readonly liveGrouping?: boolean;
}

function useNumericDraft<T extends number>(props: NumericProps<T>, kind: NumericKind<T>) {
  const { value, onValueChange, min, max } = props;
  return useDraft<T>({
    value,
    onValueChange,
    parse: kind.parse,
    format: kind.format,
    invalidMessage: kind.invalid,
    validate: (v) => {
      if (!kind.allowNegative && v < 0) return t('fields.numberNegative');
      if (kind.integerOnly && !Number.isInteger(v)) return t('fields.numberInteger');
      if (min !== undefined && v < min) return t('fields.numberTooSmall', { min: kind.show(min) });
      if (max !== undefined && v > max) return t('fields.numberTooLarge', { max: kind.show(max) });
      return null;
    },
  });
}

/**
 * Подсказка при вводе: вычисление или сокращение — «Поняли так: 3 600 ₽»; обычное число — описание
 * поля; непонятное — примеры форматов.
 */
function numericDescription<T extends number>(draft: Draft<T>, kind: NumericKind<T>, description: ReactNode) {
  if (!draft.editing) return description;
  if (draft.problem === null && draft.parsed !== null) {
    const plain = /^[−-]?[\d\s\u00a0]*([.,]\d*)?$/.test(draft.text.trim());
    return plain ? description : t('fields.understood', { value: kind.show(draft.parsed) });
  }
  return kind.hint ?? description;
}

function NumericBox<T extends number>({
  draft,
  kind,
  props,
}: {
  draft: Draft<T>;
  kind: NumericKind<T>;
  props: NumericProps<T>;
}) {
  const {
    value: _value,
    onValueChange: _change,
    min: _min,
    max: _max,
    step,
    size,
    variant,
    className,
    placeholder,
    ...state
  } = props;
  const { control, disabled, readOnly, invalid } = useBoxState(state);
  const input = useRef<HTMLInputElement | null>(null);
  const caret = useRef<number | null>(null);

  // После переформатирования (разряды) вернуть курсор туда, где человек печатал.
  useLayoutEffect(() => {
    if (caret.current !== null && input.current === document.activeElement) {
      input.current?.setSelectionRange(caret.current, caret.current);
    }
    caret.current = null;
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!kind.liveGrouping) {
      draft.setText(e.target.value);
      return;
    }
    const next = groupDigitsLive(e.target.value, e.target.selectionStart ?? e.target.value.length);
    caret.current = next.caret;
    draft.setText(next.text);
  };

  return (
    <FieldBox
      size={size}
      variant={variant}
      disabled={disabled}
      readOnly={readOnly}
      invalid={invalid || draft.error !== null}
      className={className}
      leading={kind.leading}
      trailing={kind.trailing}
    >
      <input
        ref={input}
        {...control}
        {...draft.inputProps}
        onChange={onChange}
        aria-label={props['aria-label']}
        aria-invalid={draft.error !== null || control['aria-invalid']}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        className={cn(inputClass, 'tabular-nums', variant === 'cell' && 'text-right')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && draft.editing) {
            e.preventDefault();
            draft.commit();
          } else if (e.key === 'Escape' && draft.editing) {
            e.stopPropagation();
            draft.revert();
          } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !readOnly && draft.problem === null) {
            // Стрелки: ± шаг, с Shift — ± десять шагов; пустое поле начинается с нуля.
            e.preventDefault();
            const delta = (step ?? kind.step) * (e.shiftKey ? 10 : 1) * (e.key === 'ArrowUp' ? 1 : -1);
            let next = (draft.parsed ?? 0) + delta;
            if (!kind.allowNegative) next = Math.max(next, 0);
            if (props.min !== undefined) next = Math.max(next, props.min);
            if (props.max !== undefined) next = Math.min(next, props.max);
            draft.set(kind.make(next));
          }
        }}
      />
    </FieldBox>
  );
}

/** Собрать пару «элемент ввода + поле с подписью» для вида числа. */
function numericFields<T extends number, P extends NumericProps<T>>(useKind: (props: P) => NumericKind<T>) {
  function Input(props: P) {
    const kind = useKind(props);
    const draft = useNumericDraft(props, kind);
    return <NumericBox draft={draft} kind={kind} props={props} />;
  }
  function FieldComponent(props: P & FieldPart) {
    const [field, rest] = splitField(props);
    // Уходят только необязательные свойства обёртки (подпись, состояния) — их поле берёт из `Field`.
    // Для обобщённого P TypeScript этого не выводит.
    const input = rest as unknown as P;
    const kind = useKind(input);
    const draft = useNumericDraft(input, kind);
    return (
      <Field
        {...field}
        error={field.error ?? draft.error}
        description={numericDescription(draft, kind, field.description)}
      >
        <NumericBox draft={draft} kind={kind} props={input} />
      </Field>
    );
  }
  return [Input, FieldComponent] as const;
}

export interface MoneyInputProps extends NumericProps<Kopecks> {
  /** Сумма может быть отрицательной: корректировки, возвраты. */
  readonly allowNegative?: boolean;
}

const MONEY_STEP = kopecks(10_000);

/**
 * Деньги в копейках: разряды при вводе, «₽» справа, `120к`, `1,2м`, `=1200*3`;
 * стрелки ± 100 ₽ (с Shift — ± 1 000 ₽).
 */
export const [MoneyInput, MoneyField] = numericFields<Kopecks, MoneyInputProps>((props) => ({
  parse: parseMoneyInput,
  format: formatMoneyInput,
  show: (v) => formatMoney(v),
  make: (n) => kopecks(Math.round(n)),
  invalid: t('fields.moneyInvalid'),
  hint: t('fields.moneyHint'),
  step: MONEY_STEP,
  allowNegative: props.allowNegative ?? false,
  integerOnly: true,
  trailing: t('units.rub'),
  liveGrouping: true,
}));

export type MoneyFieldProps = MoneyInputProps & FieldPart;

/** Проценты в базисных пунктах: `40`, `12,5 %`; стрелки ± 1 %. */
export const [PercentInput, PercentField] = numericFields<BasisPoints, NumericProps<BasisPoints>>(() => ({
  parse: parsePercentInput,
  format: formatPercentInput,
  show: (v) => formatPercent(v),
  make: (n) => bp(Math.round(n)),
  invalid: t('fields.percentInvalid'),
  step: bp(100),
  integerOnly: true,
  trailing: t('units.percent'),
}));

export type PercentFieldProps = NumericProps<BasisPoints> & FieldPart;

/** Коэффициент в базисных пунктах: `×1,3`, `0,85`; стрелки ± 0,05. */
export const [MultiplierInput, MultiplierField] = numericFields<BasisPoints, NumericProps<BasisPoints>>(
  () => ({
    parse: parseMultiplierInput,
    format: formatMultiplierInput,
    show: (v) => `${t('units.times')}${formatMultiplierInput(v)}`,
    make: (n) => bp(Math.round(n)),
    invalid: t('fields.multiplierInvalid'),
    step: bp(500),
    integerOnly: true,
    leading: t('units.times'),
  }),
);

export type MultiplierFieldProps = NumericProps<BasisPoints> & FieldPart;

/** Время работ в минутах: `1,5`, `1:30`, `90м`, `1ч 30м`; стрелки ± 30 минут. */
export const [HoursInput, HoursField] = numericFields<Minutes, NumericProps<Minutes>>(() => ({
  parse: parseHoursInput,
  format: formatHoursInput,
  show: (v) => formatDuration(v),
  make: (n) => minutes(Math.max(Math.round(n), 0)),
  invalid: t('fields.hoursInvalid'),
  hint: t('fields.hoursHint'),
  step: minutes(30),
  integerOnly: true,
  trailing: t('units.hour'),
}));

export type HoursFieldProps = NumericProps<Minutes> & FieldPart;

export interface NumberInputProps extends NumericProps<number> {
  /** Знаков после запятой; 0 — только целые. */
  readonly fractionDigits?: number;
  readonly allowNegative?: boolean;
  /** Единица справа: «шт.», «чел.», «стр.». */
  readonly unit?: string;
}

/** Число: разряды при вводе, границы, шаг, единица справа. */
export const [NumberInput, NumberField] = numericFields<number, NumberInputProps>((props) => ({
  parse: parseNumberInput,
  format: (v) => formatNumberInput(v, props.fractionDigits ?? 0),
  show: (v) => formatNumber(v),
  make: (n) => Number(n.toFixed(props.fractionDigits ?? 0)),
  invalid: t('fields.numberInvalid'),
  step: 1,
  allowNegative: props.allowNegative ?? false,
  integerOnly: (props.fractionDigits ?? 0) === 0,
  ...(props.unit === undefined ? {} : { trailing: props.unit }),
  liveGrouping: true,
}));

export type NumberFieldProps = NumberInputProps & FieldPart;
